import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar, Info } from 'lucide-react';
import { specialtiesFromAvailability } from '../pages/landing/specialtyMeta';
import { BookingStepRail, BookingSpecialtyStep, BookingDoctorStep } from './booking/BookingSteps';
import { BookingDateStep } from './booking/BookingFormParts';
import BookingSlotPicker from './booking/BookingSlotPicker';
import BookingConfigNote from './booking/BookingConfigNote';
import BookingDailyPlace from './booking/BookingDailyPlace';
import { buildDateStrip, indexDoctorProfiles } from './booking/bookingHelpers';
import { dateAvailabilitySets, mergeSlotGrid } from './booking/bookingAvailUtils';
import BookingDetailsFlow from './booking/BookingDetailsFlow';
import BookingIntentStep from './booking/BookingIntentStep';
import BookingReferralRedirect from './booking/BookingReferralRedirect';
import BookingSuccessCard from './booking/BookingSuccessCard';
import BookingPickSummary from './booking/BookingPickSummary';

const API = process.env.REACT_APP_BACKEND_URL;

/** Intent first → referral exit OR consulting booking wizard.
 *  `initialIntent`: skip chooser when deep-linked (e.g. patient portal Book OPD).
 *  `lockIntent`: hide “change consulting/referral” when intent was pre-chosen.
 */
export default function PublicBookingSection({
  slug, primary, preset = null, doctorProfiles = [], initialIntent = '', lockIntent = false,
  allowedCategories, prefillPatient, familyMembers, skipEmailOtp = false,
}) {
  const today = useMemo(() => new Date(), []);
  const dateStrip = useMemo(() => buildDateStrip(today), [today]);
  const fromIso = dateStrip[0].iso;
  const toIso = dateStrip[dateStrip.length - 1].iso;

  const [visitIntent, setVisitIntent] = useState(initialIntent || '');
  const [avail, setAvail] = useState([]);
  const [config, setConfig] = useState(null);
  const [specialty, setSpecialty] = useState('');
  const [picked, setPicked] = useState({ date: '', doctor_id: '', doctor_name: '', slot: '' });
  const [success, setSuccess] = useState(null);

  const reloadAvail = () => {
    axios.get(`${API}/api/tenant/${slug}/availability`, { params: { from_date: fromIso, to_date: toIso } })
      .then(({ data }) => { setAvail(data?.available || []); setConfig(data?.config || null); })
      .catch(() => { setAvail([]); setConfig(null); });
  };
  useEffect(() => { reloadAvail(); /* eslint-disable-next-line */ }, [slug, fromIso, toIso]);

  const specialtyGroups = useMemo(() => specialtiesFromAvailability(avail), [avail]);
  const profilesById = useMemo(() => indexDoctorProfiles(doctorProfiles), [doctorProfiles]);

  useEffect(() => {
    if (!preset || !avail.length || visitIntent !== 'consulting') return;
    const spec = preset.specialty || '';
    if (!spec) return;
    setSpecialty(spec);
    if (preset.doctorName) {
      const doc = specialtyGroups.find((g) => g.specialty === spec)?.doctors
        .find((d) => d.name === preset.doctorName);
      if (doc) setPicked({ date: '', doctor_id: doc.id, doctor_name: doc.name, slot: '' });
    }
  }, [preset, avail, specialtyGroups, visitIntent]);

  const doctorsForSpec = useMemo(
    () => specialtyGroups.find((g) => g.specialty === specialty)?.doctors || [],
    [specialtyGroups, specialty],
  );
  const doctorDays = useMemo(
    () => avail.filter((a) => a.doctor_id === picked.doctor_id),
    [avail, picked.doctor_id],
  );
  const { datesWithSlots, datesFullyBooked } = useMemo(
    () => dateAvailabilitySets(doctorDays),
    [doctorDays],
  );
  const dayRows = useMemo(() => {
    if (!picked.date || !picked.doctor_id) return [];
    return doctorDays.filter((a) => a.date === picked.date);
  }, [picked, doctorDays]);
  const slotsForPick = useMemo(() => {
    const slots = [];
    dayRows.forEach((r) => (r.slots || []).forEach((s) => { if (!slots.includes(s)) slots.push(s); }));
    return slots;
  }, [dayRows]);
  const slotGrid = useMemo(() => mergeSlotGrid(dayRows), [dayRows]);
  const mode = dayRows[0]?.booking_mode || config?.booking_mode || 'slots';
  const remaining = dayRows.reduce((n, r) => Math.max(n, r.remaining || 0), 0);
  const step = !specialty ? 1 : !picked.doctor_id ? 2 : !picked.slot ? 3 : 4;
  const clearPick = () => setPicked({ date: '', doctor_id: '', doctor_name: '', slot: '' });

  if (success) return <BookingSuccessCard success={success} />;

  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 sm:p-6 text-left" data-testid="public-booking">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Appointments</div>
      <div className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope' }}>
        {visitIntent === 'consulting' || initialIntent === 'consulting' ? 'Book an OPD visit' : 'Book a visit'}
      </div>
      {!visitIntent ? (
        <p className="text-[12px] text-zinc-400 mb-4 flex items-start gap-2">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: primary }} />
          <span>
            Choose <strong className="text-zinc-200">Consulting</strong> for a GP slot, or{' '}
            <strong className="text-zinc-200">Referral</strong> for the patient portal.
          </span>
        </p>
      ) : visitIntent === 'consulting' ? (
        <p className="text-[12px] text-zinc-400 mb-4">
          Select specialty, doctor and slot for your campus OPD appointment.
        </p>
      ) : null}

      {!visitIntent && (
        <BookingIntentStep primary={primary} value={visitIntent}
          onPick={(id) => { setVisitIntent(id); setSpecialty(''); clearPick(); }} />
      )}
      {visitIntent === 'referral' && (
        <BookingReferralRedirect primary={primary} onBack={lockIntent ? undefined : () => setVisitIntent('')} />
      )}
      {visitIntent === 'consulting' && (
        <>
          {!lockIntent ? (
            <button type="button" onClick={() => { setVisitIntent(''); setSpecialty(''); clearPick(); }}
              className="text-[11px] text-zinc-500 hover:text-zinc-300 mb-3">
              ← Change: Consulting / Referral
            </button>
          ) : null}
          <BookingConfigNote config={config} />
          <BookingStepRail step={step} primary={primary} />
          {step === 1 && (specialtyGroups.length === 0 ? (
            <p className="text-center text-xs text-zinc-500 italic py-6">
              <Calendar className="h-4 w-4 inline mb-0.5" /> No open booking windows yet — please call the clinic.
            </p>
          ) : (
            <BookingSpecialtyStep specialties={specialtyGroups} primary={primary}
              onPick={(s) => { setSpecialty(s); clearPick(); }} />
          ))}
          {step === 2 && (
            <BookingDoctorStep doctors={doctorsForSpec} profilesById={profilesById}
              pickedId={picked.doctor_id} primary={primary}
              onPick={(d) => setPicked({ date: '', doctor_id: d.id, doctor_name: d.name, slot: '' })}
              onBack={() => { setSpecialty(''); clearPick(); }} />
          )}
          {step >= 3 && !picked.slot && (
            <>
              <BookingDateStep dates={dateStrip} datesWithSlots={datesWithSlots}
                datesFullyBooked={datesFullyBooked} pickedDate={picked.date} primary={primary}
                onPick={(iso) => setPicked((p) => ({ ...p, date: iso, slot: '' }))} onBack={clearPick} />
              {picked.date && mode === 'daily_count' && (
                <BookingDailyPlace remaining={remaining} primary={primary}
                  onRequest={() => setPicked((p) => ({ ...p, slot: 'day' }))} />
              )}
              {picked.date && mode !== 'daily_count' && (
                <BookingSlotPicker slots={slotsForPick} slotGrid={slotGrid} pickedSlot={picked.slot}
                  primary={primary} onPick={(s) => setPicked((p) => ({ ...p, slot: s }))} />
              )}
            </>
          )}
          {picked.slot && (
            <>
              <BookingPickSummary specialty={specialty} doctorName={picked.doctor_name}
                date={picked.date} slot={picked.slot} primary={primary} />
              <BookingDetailsFlow slug={slug} primary={primary} picked={picked} visitType="consulting"
                allowedCategories={allowedCategories}
                prefillPatient={prefillPatient}
                familyMembers={familyMembers}
                skipEmailOtp={skipEmailOtp}
                onBack={() => setPicked((p) => ({ ...p, slot: '' }))}
                onSuccess={(data) => {
                  setSuccess({
                    ...data,
                    doctor_name: data.doctor_name || picked.doctor_name,
                    preferred_date: data.preferred_date || picked.date,
                    preferred_slot: data.preferred_slot || picked.slot,
                  });
                  reloadAvail();
                }} />
            </>
          )}
        </>
      )}
    </div>
  );
}
